"use client";
import { useEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { createPortal } from "react-dom";

/**
 * Modal compartido por todas las apps (web, forum, notes-app, posts-app,
 * jobs-app). Resuelve de una vez tres cosas que cada modal repetía —o no
 * hacía— por su cuenta:
 *
 * 1. **Portal a `<body>`.** Un modal renderizado donde se declara es un
 *    hermano más en el flujo, y eso tiene efectos secundarios reales: dentro
 *    de un contenedor `space-y-*` de Tailwind 4 (`& > :not(:last-child)`),
 *    abrirlo hace que el hermano anterior DEJE de ser el último y gane un
 *    margen inferior. Medido en producción el 2026-07-27 en `/calendar`: el
 *    documento crecía 32 px, el footer bajaba lo mismo y aparecía una barra
 *    de desplazamiento de página que desplazaba el contenido a la izquierda.
 *    Colgando del `<body>` no hay hermanos a los que afectar.
 *
 * 2. **Bloqueo del scroll de fondo con compensación de gutter.** Mientras el
 *    modal está abierto la página no debe desplazarse: el modal ya trae su
 *    propia barra. Al quitar la barra exterior el contenido se ensancharía y
 *    saltaría a la derecha, así que se compensa el ancho exacto de la barra
 *    con `padding-right`. El contador es global porque puede haber dos
 *    modales apilados (p. ej. un confirm sobre un modal): sólo el último en
 *    cerrarse restaura el body.
 *
 * 3. **Animación de entrada.** Antes aparecían de golpe. Fundido del velo +
 *    un leve escalado/subida del panel, con utilidades de Tailwind (nada de
 *    keyframes propios, que habría que duplicar en el CSS de cada app) y
 *    `motion-reduce:` para quien pide menos movimiento.
 */

// ---------------------------------------------------------------- scroll lock

let lockCount = 0;
let savedRootOverflow = "";
let savedBodyOverflow = "";
let savedPaddingRight = "";

function lockBodyScroll() {
  if (typeof document === "undefined") return;
  lockCount += 1;
  if (lockCount > 1) return;
  const root = document.documentElement;
  const body = document.body;
  // Ancho real de la barra de desplazamiento AHORA: 0 si el sistema usa
  // barras superpuestas (móvil, macOS por defecto), ~10-15 px en escritorio.
  const gutter = window.innerWidth - root.clientWidth;

  savedRootOverflow = root.style.overflow;
  savedBodyOverflow = body.style.overflow;
  savedPaddingRight = body.style.paddingRight;

  // ⚠ El candado va en <html>, NO sólo en <body>. `overflow` del body sólo
  // se propaga al viewport cuando el de <html> es `visible`, y el CSS global
  // de estas apps pone `html, body { overflow-x: clip }` — con eso, poner
  // `overflow:hidden` en el body NO detiene nada (comprobado: con el modal
  // abierto la página seguía haciendo scroll). Se ponen los dos.
  root.style.overflow = "hidden";
  body.style.overflow = "hidden";
  if (gutter > 0) {
    const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${current + gutter}px`;
  }
}

function unlockBodyScroll() {
  if (typeof document === "undefined") return;
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0) return;
  document.documentElement.style.overflow = savedRootOverflow;
  document.body.style.overflow = savedBodyOverflow;
  document.body.style.paddingRight = savedPaddingRight;
}

/**
 * Bloquea el scroll del fondo mientras el componente está montado, con la
 * misma compensación de gutter que usa `Modal`. Para las capas que no pueden
 * usar `Modal` porque colocan cosas en posición absoluta respecto al velo
 * (el visor de imágenes, por ejemplo).
 */
export function useBodyScrollLock() {
  useEffect(() => {
    lockBodyScroll();
    return unlockBodyScroll;
  }, []);
}

/**
 * `false` al principio y `true` un par de fotogramas después: el interruptor
 * para animar una entrada con utilidades de transición, que necesitan un
 * estado inicial ya pintado desde el que salir.
 *
 * Son DOS `requestAnimationFrame` a propósito. Con uno solo la carrera se
 * pierde a veces —el estado inicial y el final caen en el mismo fotograma y
 * el modal aparece de golpe, que es justo lo que se quería quitar—; medido
 * en el calendario, unas veces animaba y otras no. Dos fotogramas garantizan
 * que el navegador haya pintado el estado de partida.
 *
 * `ready` permite retrasar el arranque hasta que el nodo exista de verdad
 * (el portal aparece un render después del primero).
 */
export function useEnterTransition(ready = true): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ready) {
      setShown(false);
      return;
    }
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setShown(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [ready]);
  return shown;
}

// -------------------------------------------------------------------- modal

export interface ModalProps {
  /** Cierre pedido por Escape o por pulsar fuera del panel. */
  onClose: () => void;
  /** Nombre accesible del diálogo. Usa `labelledBy` si el título ya está en el DOM. */
  label?: string;
  labelledBy?: string;
  /** Clases del PANEL (la caja blanca/negra), no del velo. */
  className?: string;
  /** Clases extra del velo, para alinear arriba en vez de al centro, etc. */
  overlayClassName?: string;
  /** `false` deja el modal sin cierre por clic en el velo (formularios largos). */
  closeOnBackdrop?: boolean;
  /** `false` deja el Escape al llamante (p. ej. un diálogo con semántica propia). */
  closeOnEscape?: boolean;
  /**
   * Referencia al velo, para quien necesite medirlo o marcar `inert`
   * alrededor. Acepta una función porque el velo aparece un render DESPUÉS
   * del primero (el portal necesita `document`): con un objeto ref el
   * llamante no se entera de cuándo aparece, y un efecto que lea
   * `ref.current` lo encontraría vacío.
   */
  overlayRef?: Ref<HTMLDivElement>;
  children: ReactNode;
}

export function Modal({
  onClose,
  label,
  labelledBy,
  className = "",
  overlayClassName = "",
  closeOnBackdrop = true,
  closeOnEscape = true,
  overlayRef,
  children,
}: ModalProps) {
  // El portal necesita `document`, que no existe en el render del servidor.
  const [mounted, setMounted] = useState(false);
  // La animación no arranca hasta que el portal está montado: antes no hay
  // nodo en el DOM que pueda transicionar.
  const shown = useEnterTransition(mounted);
  const localRef = useRef<HTMLDivElement | null>(null);
  const ref = overlayRef ?? localRef;

  useBodyScrollLock();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeOnEscape, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 transition-opacity duration-200 ease-out motion-reduce:transition-none ${
        shown ? "opacity-100" : "opacity-0"
      } ${overlayClassName}`}
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`transition duration-200 ease-out motion-reduce:transition-none ${
          shown ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-95 opacity-0"
        } ${className}`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
